import {
  Form,
  FormRow,
  Option, 
  PopupHint, 
  SegmentedButton,
  Select
} from '@op/opds.opux';
import { useFormValidationAlert } from '@op/luna.yaweb-common-js/FormValidationAlert';
import { Message, useMessageGetter } from '@messageformat/react';
import { IO_CHANGE_INSURANCE } from '../../constants/actionTypes';
import cities from '../../constants/cities';
import { DEFAULT_LOCALE } from '../../constants/common';
import enginebrands from '../../constants/enginebrands';
import { useInsuranceOffer } from '../../hooks';
import { buildElementId } from '../../utils';
import ValidationError from '../common/ValidationError/ValidationError';
import { FormInput } from '../common/Input/FormInput';

const BoatQuestions = (insurance) => {
  const { state, validateInsurance } = useInsuranceOffer();
  const getMessage = useMessageGetter();
  const getDefaultMessage = useMessageGetter([], { locale: DEFAULT_LOCALE });
  const { errors, removeErrors } = useFormValidationAlert();
  const newErrors = { add: [], remove: [] };
  const boats = ['Motorboat', 'Motor sailor', 'Sailboat'];
  const motorBoats = ['Motorboat', 'Motor sailor'];
  const index = state.selectedInsurances.findIndex(
    (element) => element.id === insurance.id
  );

  const boatDetails = state.selectedInsurances[index].questions;

  const makeInputData = (data, label, answer, errorLabel) => {
    const value = data || '';
    return {
      value,
      dataKey: `${insurance.id}-${label}`,
      question: getMessage(`labelText.${label}`),
      answer: answer ? getMessage(answer) : value,
      defaultQuestion: getDefaultMessage(`labelText.${label}`),
      defaultAnswer: answer ? getDefaultMessage(answer) : value,
      errorLabel: errorLabel
        ? `${insurance.caption} : ${getMessage(
            `labelText.${label}`
          )} - ${getMessage(errorLabel)}`
        : '',
      getMessage,     
    };
  };
  
  const makeInputDataForEngine = (data, label, answer, questionText, errorLabel) => {
    const value = data || '';    
    return {
      value,
      dataKey: `${insurance.id}-${label}`,
      question: `${questionText}`,
      answer: answer ? getMessage(answer) : value,
      defaultQuestion: getDefaultMessage(`labelText.${label}`),
      defaultAnswer: answer ? getDefaultMessage(answer) : value,
      errorLabel: errorLabel
        ? `${insurance.caption} : ${getMessage(
            `labelText.${label}`
          )} - ${getMessage(errorLabel)}`
        : '',
      getMessage,
    };
  };
  const segmentLength = 3;

  const getValue = (answer) => {
    return (answer || { value: '' }).value;
  };

  const getOptionTitle = (event, key) => {
    const { value, selectedIndex } = event.target;
    return value === 'choose'
      ? 'uiText.choose'
      : `option.${key}.${String(selectedIndex).padStart(3, '0')}`;
  };

  const cleanInputsByIds = (arrayList, errs, insId) => {
      return arrayList
        .map((item) => buildElementId(insId, item)) // Create full IDs
        .filter((fullId) => errs.some((err) => err.id === fullId)); // Filter existing ones
    };

  return (
    <Form spacing="default">
      <div>
        <FormRow
          dataKey={`${insurance.id}-boatType`}
          label={<Message id="labelText.boatType" />}
          labelFor={buildElementId(insurance.id, 'BOAT_TYPE')}
          required
        >
          <Select
            value={getValue(boatDetails.boatType)}
            aria-describedby='boatTypeError'
            name={`${insurance.id}-boatType`}
            onChange={(e) => {
              newErrors.remove = errors
                .filter((record) => record.id.startsWith(insurance.id))
                .map((record) => record.id);
              removeErrors(...newErrors.remove);
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: {
                  boatType: makeInputData(
                    e.target.value,
                    'boatType',
                    getOptionTitle(e, 'boatType'),
                    'error.fill.in.missing.information'
                  ),
                },
                questionId: e.target.id,
              });
            }}
            id={buildElementId(insurance.id, 'BOAT_TYPE')}
          >
            <option value="choose" key="choose">
              {getMessage('uiText.choose')}
            </option>
            <option value="Motorboat" key="Motorboat">
              {getMessage('option.boatType.001')}
            </option>
            <option value="Motor sailor" key="Motor sailor">
              {getMessage('option.boatType.002')}
            </option>
            <option value="Sailboat" key="Sailboat">
              {getMessage('option.boatType.003')}
            </option>
          </Select>

          <ValidationError
            id='boatTypeError'
            requireVisible={false}
            errorId={buildElementId(insurance.id, 'BOAT_TYPE')}
            errorMessageId="error.fill.in.missing.information"
          />
        </FormRow>
      </div>
	  
      {getValue(boatDetails.boatType) === 'Motorboat' && (
        <div>
          <FormRow
            dataKey={`${insurance.id}-motorBoatType`}
            label={<Message id="labelText.motorBoatType" />}
            labelFor={buildElementId(insurance.id, 'BOAT_MOTORBOATTYPE')}
            required
          >
            <Select
              value={getValue(boatDetails.motorBoatType)}
              aria-describedby='motorBoatTypeError'
              name={`${insurance.id}-motorBoatType`}
              onChange={(e) => {
                boatDetails.motorBoatType = makeInputData(
                  e.target.value,
                  'motorBoatType',
                  getOptionTitle(e, 'motorBoatType'),
                  'error.fill.in.missing.information'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: boatDetails,
                  questionId: e.target.id,
                });
              }}
              id={buildElementId(insurance.id, 'BOAT_MOTORBOATTYPE')}
            >
              <option value="choose" key="choose">
                {getMessage('uiText.choose')}
              </option>
              <option value="option_1" key="option_1">
                {getMessage('option.motorBoatType.001')}
              </option>
              <option value="option_2" key="option_2">
                {getMessage('option.motorBoatType.002')}
              </option>
              <option value="option_3" key="option_3">
                {getMessage('option.motorBoatType.003')}
              </option>             
            </Select>

            <ValidationError
              id='motorBoatTypeError'
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'BOAT_MOTORBOATTYPE')}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>
        </div>
      )}
	  
      {boats.indexOf(getValue(boatDetails.boatType)) > -1 && (
        <div>
          <FormInput
            type="stringwithoutSP"
            value={getValue(boatDetails.make)}
            inputId={buildElementId(insurance.id, 'BOAT_MAKE')}
            onChange={(make , error) => {
              boatDetails.make = makeInputData(
                make,
                'make'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: boatDetails,
                questionId: `${insurance.id}-make`,
                error,
              });                
            }}
            label={getMessage('labelText.make')}
            required
          />

          <FormInput
            type="stringwithoutSP"
            value={getValue(boatDetails.model)}
            inputId={buildElementId(insurance.id, 'BOAT_MODEL')}
            onChange={(model , error) => {
              boatDetails.model = makeInputData(
                model,
                'model'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: boatDetails,
                questionId: `${insurance.id}-model`,
                error,
              });                
            }}
            label={getMessage('labelText.model')}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.model" />
              </PopupHint>
            }
            required
          />         
          <FormRow
            dataKey={`${insurance.id}-frameMaterial`}
            label={<Message id="labelText.frameMaterial" />}
            labelFor={buildElementId(insurance.id, 'BOAT_FRAMEMATERIAL')}
            required
          >
            <Select
              value={getValue(boatDetails.framematerial)}
              aria-describedby='frameMaterialError'
              name={`${insurance.id}-framematerial`}
              onChange={(e) => {
                boatDetails.framematerial = makeInputData(
                  e.target.value,
                  'frameMaterial',
                  getOptionTitle(e, 'frameMaterial'),
                  'error.fill.in.missing.information'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: boatDetails,
                  questionId: e.target.id,
                });
              }}
              id={buildElementId(insurance.id, 'BOAT_FRAMEMATERIAL')}
            >
              <option value="choose" key="choose">
                {getMessage('uiText.choose')}
              </option>
              <option value="Aluminium " key="Aluminium">
                {getMessage('option.frameMaterial.001')}
              </option>
              <option value="Carbon fibre" key="Carbon fibre">
                {getMessage('option.frameMaterial.002')}
              </option>
              <option value="Rubber" key="Rubber">
                {getMessage('option.frameMaterial.003')}
              </option>
              <option value="Glass fibre" key="Glass fibre">
                {getMessage('option.frameMaterial.004')}
              </option>
              <option value="Composite plastic" key="Composite plastic">
                {getMessage('option.frameMaterial.005')}
              </option>
              <option value="Wood" key="Wood">
                {getMessage('option.frameMaterial.006')}
              </option>
              <option value="Steel" key="Steel">
                {getMessage('option.frameMaterial.007')}
              </option>
              <option
                value="Glass fibre-reinforced wood"
                key="Glass fibre-reinforced wood"
              >
                {getMessage('option.frameMaterial.008')}
              </option>
            </Select>

            <ValidationError
              id='frameMaterialError'
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'BOAT_FRAMEMATERIAL')}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>

          <FormRow
            dataKey={`${insurance.id}-manufacturingMethod`}
            label={<Message id="labelText.manufacturingMethod" />}
            labelFor={buildElementId(insurance.id, 'BOAT_MANUFACTURINGMETHOD')}
            required
          >
            <Select
              value={getValue(boatDetails.manufacturingMethod)}
              aria-describedby='manufacturingMethodError'
              name={`${insurance.id}-manufacturingMethod`}
              onChange={(e) => {
                boatDetails.manufacturingMethod = makeInputData(
                  e.target.value,
                  'manufacturingMethod',
                  getOptionTitle(e, 'manufacturingMethod'),
                  'error.fill.in.missing.information'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: boatDetails,
                  questionId: e.target.id,
                });
              }}
              id={buildElementId(insurance.id, 'BOAT_MANUFACTURINGMETHOD')}
            >
              <option value="choose" key="choose">
                {getMessage('uiText.choose')}
              </option>
              <option value="Factory-made" key="Factory-made">
                {getMessage('option.manufacturingMethod.001')}
              </option>
              <option value="Self-made" key="Self-made">
                {getMessage('option.manufacturingMethod.002')}
              </option>
            </Select>

            <ValidationError
              id='frameMaterialError'
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'BOAT_MANUFACTURINGMETHOD')}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>

          <FormInput
            type="year"
            limit={1800}
            maxLength={4}
            value={getValue(boatDetails.manufactureYear)}
            inputId={buildElementId(insurance.id, 'BOAT_MANUFACTUREYEAR')}
            onChange={(manufactureYear , error) => {
              boatDetails.manufactureYear = makeInputData(
                manufactureYear,
                'manufactureYear'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: boatDetails,
                questionId: `${insurance.id}-manufactureYear`,
                error,
              });                
            }}
            label={getMessage('labelText.manufactureYear')}
            required
          />
          
          <FormRow
            dataKey={`${insurance.id}-inspectionClass`}
            label={<Message id="labelText.inspectionClass" />}
            labelFor={buildElementId(insurance.id, 'BOAT_INSPECTIONCLASS')}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.inspectionClass" />
              </PopupHint>
            }
            required
          >
            <Select
              value={getValue(boatDetails.inspectionclass)}
              aria-describedby='inspectionClassError'
              name={`${insurance.id}-inspectionclass`}
              onChange={(e) => {
                boatDetails.inspectionclass = makeInputData(
                  e.target.value,
                  'inspectionClass',
                  getOptionTitle(e, 'inspectionClass'),
                  'error.fill.in.missing.information'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: boatDetails,
                  questionId: e.target.id,
                });
              }}
              id={buildElementId(insurance.id, 'BOAT_INSPECTIONCLASS')}
            >
              <option value="choose" key="choose">
                {getMessage('uiText.choose')}
              </option>
              <option value="No inspection class" key="No inspection class">
                {getMessage('option.inspectionClass.001')}
              </option>
              <option value="Inspection class 1" key="Inspection class 1">
                {getMessage('option.inspectionClass.002')}
              </option>
              <option value="Inspection class 2" key="Inspection class 2">
                {getMessage('option.inspectionClass.003')}
              </option>
              <option value="Inspection class 3" key="Inspection class 3">
                {getMessage('option.inspectionClass.004')}
              </option>
              <option value="Condition survey" key="Condition survey">
                {getMessage('option.inspectionClass.005')}
              </option>
            </Select>

            <ValidationError
              id='inspectionClassError'
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'BOAT_INSPECTIONCLASS')}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>
          {getValue(boatDetails.boatType) === 'Sailboat' && (
            <div>
              <FormRow
                dataKey={`${insurance.id}-boatEngine`}
                label={<Message id="labelText.boatEngine" />}
                labelFor={buildElementId(insurance.id, 'BOAT_BOATENGINE')}
              >
                <SegmentedButton
                  value={getValue(boatDetails.boatEngine)}
                  aria-describedby='boatEngineError'
                  name={`${insurance.id}-boatEngine`}
                  onChange={(e) => {
                    boatDetails.boatEngine = makeInputData(
                      e.target.value,
                      'boatEngine',
                      `buttonText.${e.target.value.toLowerCase()}`
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: boatDetails,
                      questionId: buildElementId(
                        insurance.id,
                        'BOAT_BOATENGINE'
                      ),
                    });
                  }}
                  id={buildElementId(insurance.id, 'BOAT_BOATENGINE')}
                >
                  <Option value="Yes" key="Yes" required>
                    {getMessage('buttonText.yes')}
                  </Option>
                  <Option value="No" key="No">
                    {getMessage('buttonText.no')}
                  </Option>
                </SegmentedButton>
                <ValidationError
                  id='boatEngineError'
                  requireVisible={false}
                  errorId={buildElementId(insurance.id, 'BOAT_BOATENGINE')}
                  errorMessageId="error.fill.in.missing.information"
                />
              </FormRow>
            </div>
          )}
          {(motorBoats.indexOf(getValue(boatDetails.boatType)) > -1 ||
            getValue(boatDetails.boatEngine) === 'Yes') && (
            <div>			  
              <FormRow
                dataKey={`${insurance.id}-primaryEngines`}
                label={<Message id="labelText.primaryEngines" />}
                labelFor={buildElementId(insurance.id, 'BOAT_PRIMARYENGINES')}
              >
                <SegmentedButton
                  value={getValue(boatDetails.primaryengines)}
                  aria-describedby='primaryEnginesError'
                  name={`${insurance.id}-primaryengines`}
                  segmentMinWidth="6.5rem"
                  onChange={(e) => {
                    boatDetails.primaryengines = makeInputData(
                      e.target.value,
                      'primaryEngines',
                      `${
                        { 1: 'uiText.one', 2: 'uiText.two', 3: 'uiText.three' }[
                          e.target.value
                        ]
                      }`
                    );
                    Array(segmentLength)
                    ?.fill()
                    ?.map((item, index) => {
                      newErrors.remove = errors
                        .filter((record) => record.id.startsWith(insurance.id+`_${index+1}`))
                        .map((record) => record.id);
                      removeErrors(...newErrors.remove);
                      return item;
                    })
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: boatDetails,
                      questionId: buildElementId(
                        insurance.id,
                        'BOAT_PRIMARYENGINES'
                      ),
                    });
                  }}
                  id={buildElementId(insurance.id, 'BOAT_PRIMARYENGINES')}
                >
                  <Option value="1" key="1" required>
                    {getMessage('uiText.one')}
                  </Option>
                  <Option value="2" key="2">
                    {getMessage('uiText.two')}
                  </Option>
                  <Option value="3" key="3">
                    {getMessage('uiText.three')}
                  </Option>
                </SegmentedButton>
                <ValidationError
                  id='primaryEnginesError'
                  requireVisible={false}
                  errorId={buildElementId(insurance.id, 'BOAT_PRIMARYENGINES')}
                  errorMessageId="error.fill.in.missing.information"
                />
              </FormRow>

			  {Array(+getValue(boatDetails.primaryengines))
				?.fill()
				?.map((item, index) => {
					const key = index+1;
				return (
        <div key={`BOAT_PRIMARYENGINES_div_${key}`}>
        <FormRow label={<h3 className="RS-insurance-questions-header">
				{`${key}. ${getMessage('uiText.engineData')}`}</h3>} />			    
				<FormRow
				   dataKey={`${insurance.id}-engineBrand-${key}`}
				   label={<Message id="labelText.engineBrand" />}
				   labelFor={buildElementId(insurance.id, `${key}_BOAT_ENGINEBRAND`)}
           required
				  >
          <Select
            value={getValue(boatDetails[`engineBrand-${key}`])}
            aria-describedby='engineBrandError'
            name={`${insurance.id}-engineBrand-${key}`}
            onChange={(e) => {
            boatDetails[`engineBrand-${key}`] = makeInputDataForEngine(
              e.target.value,
              `engineBrand-${key}`,
              getOptionTitle(e, 'enginebrand'),
              `${key}. ${getMessage('labelText.engineBrand')}`,
              'error.fill.in.missing.information'
            );
            validateInsurance({
              insurance,
              type: IO_CHANGE_INSURANCE,
              questions: boatDetails,
              questionId: e.target.id,
            });
            }}
            id={buildElementId(insurance.id, `${key}_BOAT_ENGINEBRAND`)}
          >
          <option value="choose" key="choose">
            {getMessage('uiText.choose')}
          </option>
            <OptionMaker dataList={enginebrands} getMessage={getMessage} />				  			  
          </Select>
          <ValidationError
            id='engineBrandError'
            requireVisible={false}
            errorId={buildElementId(insurance.id, `${key}_BOAT_ENGINEBRAND`)}
            errorMessageId="error.fill.in.missing.information"
          />
			  </FormRow>
        <FormInput
            type="stringwithoutSP"
            value={getValue(boatDetails[`engineProductionNumber-${key}`])}
            inputId={buildElementId(insurance.id, `${key}_BOAT_ENGINEPRODUCTIONNUMBER`)}   
            onChange={(value, error) => {
                boatDetails[`engineProductionNumber-${key}`] = makeInputDataForEngine(
                value,
                `engineProductionNumber-${key}`,
                undefined,
                `${key}. ${getMessage('labelText.engineProductionNumber')}`
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: boatDetails,
                questionId: `${insurance.id}-engineProductionNumber-${key}`,
                error,
              });
            }}
            label={getMessage('labelText.engineProductionNumber')}
            required
        />

        <FormInput
            type="year"
            limit={1800}
            maxLength={4}
            value={getValue(boatDetails[`engineYearOfManufacture-${key}`])}
            inputId={buildElementId(insurance.id, `${key}_BOAT_ENGINEYEAROFMANUFACTURE`)}      
            onChange={(value, error) => {
                boatDetails[`engineYearOfManufacture-${key}`] = makeInputDataForEngine(
                value,
                `engineYearOfManufacture-${key}`,
                undefined,
                `${key}. ${getMessage('labelText.engineYearOfManufacture')}`
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: boatDetails,
                questionId: `${insurance.id}-engineYearOfManufacture-${key}`,
                error,
              });
            }}
            label={getMessage('labelText.engineYearOfManufacture')}
            required
        />

        <FormInput
            type="numeric"
            limit={9999}
            amount
            unit={<Message id="unitText.horsePower" />}
            unitPos="outside"
            value={getValue(boatDetails[`enginePower-${key}`])}
            inputId={buildElementId(insurance.id, `${key}_BOAT_ENGINEPOWER`)}           
            onChange={(value, error) => {
                delete boatDetails[`registrationNumber-${key}`];

                const clearInputs = [`${key}_BOAT_REGISTRATIONNUMBER`]
                newErrors.remove = [...newErrors.remove, ...cleanInputsByIds(clearInputs, errors, insurance.id)]
                removeErrors(...newErrors.remove)

                boatDetails[`enginePower-${key}`] = makeInputDataForEngine(
                value,
                `enginePower-${key}`,
                undefined,
                `${key}. ${getMessage('labelText.enginePower')}`
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: boatDetails,
                questionId: `${insurance.id}-enginePower-${key}`,
                error,
              });
            }}
            label={getMessage('labelText.enginePower')}
            required
        />
        {Number(getValue(boatDetails[`enginePower-${key}`])) >= 21 && getValue(boatDetails.boatType) !== 'Sailboat' &&
          <FormInput
            type="stringwithoutSP"
            maxLength={20}
            value={getValue(boatDetails[`registrationNumber-${key}`])}
            inputId={buildElementId(insurance.id, `${key}_BOAT_REGISTRATIONNUMBER`)}   
            onChange={(value, error) => {
                boatDetails[`registrationNumber-${key}`] = makeInputDataForEngine(
                value,
                `registrationNumber-${key}`,
                undefined,
                `${key}. ${getMessage('labelText.registrationNumber')}`
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: boatDetails,
                questionId: `${insurance.id}-registrationNumber-${key}`,
                error,
              });
            }}
            label={getMessage('labelText.registrationNumber')}
            required
            hint={
              <PopupHint placement="top">
                <Message id="hintText.registrationNumber" />
              </PopupHint>
            }
          />
        }
        
        
        </div>);
})}
			  
            </div>
          )}

          <div>
            <FormRow
              dataKey={`${insurance.id}-damageCover`}
              label={<Message id="labelText.damageCover" />}
              labelFor={buildElementId(insurance.id, 'BOAT_DAMAGECOVER')}
              hint={
                <PopupHint placement="top">
                  <Message id="hintText.damageCover" />
                </PopupHint>
              }
            >
              <SegmentedButton
                value={getValue(boatDetails.damagecover)}
                aria-describedby='damageCoverError'
                name={`${insurance.id}-damagecover`}
                onChange={(e) => {
                  boatDetails.damagecover = makeInputData(
                    e.target.value,
                    'damageCover',
                    `buttonText.${e.target.value.toLowerCase()}`
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: boatDetails,
                    questionId: buildElementId(
                      insurance.id,
                      'BOAT_DAMAGECOVER'
                    ),
                  });
                }}
                id={buildElementId(insurance.id, 'BOAT_DAMAGECOVER')}
              >
                <Option value="Yes" key="Yes" required>
                  {getMessage('buttonText.yes')}
                </Option>
                <Option value="No" key="No">
                  {getMessage('buttonText.no')}
                </Option>
              </SegmentedButton>
              <ValidationError
                id='damageCoverError'
                requireVisible={false}
                errorId={buildElementId(insurance.id, 'BOAT_DAMAGECOVER')}
                errorMessageId="error.fill.in.missing.information"
              />
            </FormRow>
          </div>
          <FormInput
            type="numeric"
            limit={9999999}
            value={getValue(boatDetails.value)}
            inputId={buildElementId(insurance.id, 'BOAT_VALUE')}
            onChange={(value , error) => {
              boatDetails.value = makeInputData(
                value,
                'boatValue'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: boatDetails,
                questionId: `${insurance.id}-boatValue`,
                error,
              });                
            }}
            label={getMessage('labelText.boatValue')}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.boatValue" />
              </PopupHint>
            }
            required
          />         
          <FormRow
            dataKey={`${insurance.id}-boatMunicipality`}
            label={<Message id="labelText.boatMunicipality" />}
            labelFor={buildElementId(insurance.id, 'BOAT_MUNICIPALITY')}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.boatMunicipality" />
              </PopupHint>
            }
            required
          >
            <Select
              value={getValue(boatDetails.municipality)}
              aria-describedby='boatMunicipalityError'
              name={`${insurance.id}-boatMunicipality`}
              onChange={(e) => {
                boatDetails.municipality = makeInputData(
                  e.target.value,
                  'boatMunicipality',
                  (
                    cities[e.target.selectedIndex - 1] || {
                      text: 'uiText.choose',
                    }
                  ).text,
                  'error.fill.in.missing.information'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: boatDetails,
                  questionId: e.target.id,
                });
              }}
              id={buildElementId(insurance.id, 'BOAT_MUNICIPALITY')}
            >
              <option value="choose" key="choose">
                {getMessage('uiText.choose')}
              </option>

              {cities.map((item) => {
                return (
                  <option key={item.value} value={item.value}>
                    {getMessage(item.text)}
                  </option>
                );
              })}
            </Select>
            <ValidationError
              id='boatMunicipalityError'
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'BOAT_MUNICIPALITY')}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>
          {getValue(boatDetails.boatType) === 'Sailboat' && (
            <FormRow
              dataKey={`${insurance.id}-boatUsedInCompetitionsOrRaces`}
              label={<Message id="labelText.boatUsedInCompetitionsOrRaces" />}
              labelFor={buildElementId(insurance.id, 'BOAT_USEDINCOMPETITIONSORRACES')}
              hint={
                <PopupHint placement="top">
                  <Message id="hintText.boatUsedInCompetitionsOrRaces" />
                </PopupHint>
              }
              >
              <SegmentedButton
                value={getValue(boatDetails.boatUsedInCompetitionsOrRaces)}
                aria-describedby='boatUsedInCompetitionsOrRacesError'
                name={`${insurance.id}-boatUsedInCompetitionsOrRacesCompany`}
                onChange={(e) => {
                  boatDetails.boatUsedInCompetitionsOrRaces = makeInputData(
                    e.target.value,
                    'boatUsedInCompetitionsOrRaces',
                    `buttonText.${e.target.value.toLowerCase()}`
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: boatDetails,
                    questionId: buildElementId(
                      insurance.id,
                      'BOAT_USEDINCOMPETITIONSORRACES'
                    ),
                  });
                }}
                id={buildElementId(insurance.id, 'BOAT_USEDINCOMPETITIONSORRACES')}
              >
                <Option value="Yes" key="Yes" required>
                  {getMessage('buttonText.yes')}
                </Option>
                <Option value="No" key="No">
                  {getMessage('buttonText.no')}
                </Option>
              </SegmentedButton>
              <ValidationError
                id='boatUsedInCompetitionsOrRacesError'
                requireVisible={false}
                errorId={buildElementId(insurance.id, 'BOAT_USEDINCOMPETITIONSORRACES')}
                errorMessageId="error.fill.in.missing.information"
              />
            </FormRow>
          )}
          
          <FormRow
              dataKey={`${insurance.id}-ownedByFinanceCompany`}
              label={<Message id="labelText.ownedByFinanceCompany" />}
              labelFor={buildElementId(insurance.id, 'BOAT_OWNEDBYFINANCECOMPANY')}
            >
              <SegmentedButton
                value={getValue(boatDetails.ownedByFinanceCompany)}
                aria-describedby='ownedByFinanceCompanyError'
                name={`${insurance.id}-ownedByFinanceCompany`}
                onChange={(e) => {
                  delete boatDetails.financeCompany;

                  const clearInputs = ["BOAT_FINANCECOMPANY"]
                  newErrors.remove = [...newErrors.remove, ...cleanInputsByIds(clearInputs, errors, insurance.id)]
                  removeErrors(...newErrors.remove)

                  boatDetails.ownedByFinanceCompany = makeInputData(
                    e.target.value,
                    'ownedByFinanceCompany',
                    `buttonText.${e.target.value.toLowerCase()}`
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: boatDetails,
                    questionId: buildElementId(
                      insurance.id,
                      'BOAT_OWNEDBYFINANCECOMPANY'
                    ),
                  });
                }}
                id={buildElementId(insurance.id, 'BOAT_OWNEDBYFINANCECOMPANY')}
              >
                <Option value="Yes" key="Yes" required>
                  {getMessage('buttonText.yes')}
                </Option>
                <Option value="No" key="No">
                  {getMessage('buttonText.no')}
                </Option>
              </SegmentedButton>
              <ValidationError
                id='ownedByFinanceCompanyError'
                requireVisible={false}
                errorId={buildElementId(insurance.id, 'BOAT_OWNEDBYFINANCECOMPANY')}
                errorMessageId="error.fill.in.missing.information"
              />
            </FormRow>
            {getValue(boatDetails.ownedByFinanceCompany) === 'Yes' && (
              <FormRow
              dataKey={`${insurance.id}-boatFinanceCompany`}
              label={<Message id="labelText.boatFinanceCompany" />}
              labelFor={buildElementId(insurance.id, 'BOAT_FINANCECOMPANY')}
              required
            >
              <Select
                value={getValue(boatDetails.financeCompany)}
                aria-describedby='boatFinanceCompanyError'
                name={`${insurance.id}-boatFinanceCompany`}
                onChange={(e) => {
                  boatDetails.financeCompany = makeInputData(
                    e.target.value,
                    'boatFinanceCompany',
                    "",
                    'error.fill.in.missing.information'
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: boatDetails,
                    questionId: e.target.id,
                  });
                }}
                id={buildElementId(insurance.id, 'BOAT_FINANCECOMPANY')}
              >
                <option value="choose" key="choose">
                  {getMessage('uiText.choose')}
                </option>
                <option value="0199920-7" key="0199920-7">OPn autorahoitus (OP Yrityspankki Oyj) (0199920-7)</option>
                <option value="2181702-8">Aktia Pankki Oyj (2181702-8)</option>
                <option value="2460633-0">Arval Oy (2460633-0)</option>
                <option value="2682300-5">Autocredit Oy (2682300-5)</option>
                <option value="0643850-5">Axus Finland Oy (0643850-5)</option>
                <option value="2292157-2">Bigbank As Suomen Sivuliike (2292157-2)</option>
                <option value="1078693-2">Danske Bank A/S, 'Suomen sivuliike (1078693-2)</option>
                <option value="1921185-2">De Lage Landen Finans Ab, Suomen Sivuliike (1921185-2)</option>
                <option value="2960538-8">DNB Auto Finance Oy (2960538-8)</option>
                <option value="1440371-6">Drivalia Lease Finland Oy (1440371-6)</option>
                <option value="0964784-7">FCE Bank Plc (0964784-7)</option>
                <option value="0628941-5">Forso Finance Oy (0628941-5)</option>
                <option value="0768991-2">GMAC Rahoitus Oy (0768991-2)</option>
                <option value="0112308-8">Handelsbanken Finans Abp (0112308-8)</option>
                <option value="2275700-4">Ikano Bank SE Finland filial (2275700-4)</option>
                <option value="2865656-2">K Auto Leasing Oy (2865656-2)</option>
                <option value="2925958-5">Lease Com Finland Oyj (2925958-5)</option>
                <option value="3159783-1">LT Autohallinto Designated Activity Company (3159783-1)</option>
                <option value="3208346-1">LT Autohallinto II Designated Activity Company (3208346-1)</option>
                <option value="3236648-8">LT Autohallinto III Designated Activity Company (3236648-8)</option>
                <option value="3288174-6">LT Autohallinto IV Designated Activity Company (3288174-6)</option>
                <option value="3329125-4">LT Autohallinto V Designated Activity Company (3329125-4)</option>
                <option value="3373964-5">LT Autohallinto VI Designated Activity Company (3373964-5)</option>
                <option value="3437124-8">LT Autohallinto VII Designated Activity Company (3437124-8)</option>
                <option value="2856773-8">Lähitapiola Rahoitus Oy (2856773-8)</option>
                <option value="0996040-3">MS-Rahoitus Oy (0996040-3)</option>
                <option value="2006935-5">NF Fleet Oy (2006935-5)</option>
                <option value="0112305-3">Nordea Rahoitus Suomi (0112305-3)</option>
                <option value="2076455-0">Santander Consumer Finance Oy (2076455-0)</option>
                <option value="1945493-4">SAV-Rahoitus Oyj (1945493-4)</option>
                <option value="1088321-4">Scania Finans Aktiebolag (1088321-4)</option>
                <option value="3226642-8">Scf Ajoneuvohallinta X Limited (3226642-8)</option>
                <option value="3150833-3">Scf Ajoneuvohallinto IX Limited (3150833-3)</option>
                <option value="2933573-6">Scf Ajoneuvohallinto VII Limited (2933573-6)</option>
                <option value="3009889-7">Scf Ajoneuvohallinto VIII Limited (3009889-7)</option>
                <option value="3275174-9">Scf Ajoneuvohallinto XI Limited (3275174-9)</option>
                <option value="3353475-4">SCF Ajoneuvohallinto XII Limited (3353475-4)</option>
                <option value="3434938-2">SCF Ajoneuvohallinto XIII Limited (3434938-2)</option>
                <option value="2008697-2">Siemens Financial Services Ab (2008697-2)</option>
                <option value="3237195-7">Svea Bank AB (3237195-7)</option>
                <option value="1019666-8">Toyota Finance Finland Oy (1019666-8)</option>
                <option value="1511187-0">VFS Finland Ab (1511187-0)</option>
                <option value="OTHER">Muu</option>
              </Select>
              <ValidationError
                id='boatFinanceCompanyError'
                requireVisible={false}
                errorId={buildElementId(insurance.id, 'BOAT_FINANCECOMPANY')}
                errorMessageId="error.fill.in.missing.information"
              />
            </FormRow>
            )}
        </div>
      )}
    </Form>
  );
};

const OptionMaker = ({ dataList, getMessage }) =>
  dataList.map(({ value, text }) => (
    <option value={value} key={value}>
      {getMessage(text)}
    </option>
  ));

export default BoatQuestions;
